import React, { useState } from "react";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import { makeStyles } from "@material-ui/core/styles";
import { P } from "./constants";
import { ini } from "./helpers";
import { Card } from "./ui";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: 12 },

  // Employee of Month banner
  banner: { background: P.nv, borderRadius: 10, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  bannerEyebrow: { fontWeight: 600, letterSpacing: ".08em", color: "rgba(255,255,255,0.6)", marginBottom: 4 },
  bannerName: { fontWeight: 800, color: "#fff", marginBottom: 2 },
  bannerSub: { color: "rgba(255,255,255,0.75)" },
  bannerRight: { textAlign: "right" },
  bannerRewardLabel: { color: "rgba(255,255,255,0.6)", marginBottom: 4 },
  bannerReward: { fontWeight: 800, color: "#fff" },
  bannerRewardSub: { color: "rgba(255,255,255,0.6)" },

  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },

  // Tables
  table: { width: "100%", borderCollapse: "collapse" },
  th: { fontWeight: 600, color: "var(--color-text-secondary)", textAlign: "left", padding: "4px 8px", borderBottom: "0.5px solid var(--color-border-tertiary)", letterSpacing: ".04em" },
  row: { borderBottom: "0.5px solid var(--color-border-tertiary)" },
  cell: { padding: "8px 8px", borderBottom: "none" },
  cellRank: { padding: "8px 8px", borderBottom: "none", fontWeight: 700 },
  cellMono: { padding: "8px 8px", borderBottom: "none", fontFamily: "var(--font-mono)", color: "var(--color-text-secondary)" },
  cellMonoBold: { padding: "8px 8px", borderBottom: "none", fontWeight: 600, fontFamily: "var(--font-mono)" },
  cellScore: { padding: "8px 8px", borderBottom: "none", fontWeight: 700, fontFamily: "var(--font-mono)" },
  cellUpsells: { padding: "8px 8px", borderBottom: "none", fontWeight: 700, fontFamily: "var(--font-mono)" },

  nameCell: { display: "flex", alignItems: "center", gap: 7 },
  avatar: { width: 24, height: 24, borderRadius: "50%", background: "#E6F4E8", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#2E7D32" },
  avatarSm: { width: 22, height: 22, borderRadius: "50%", background: "#E6F4E8", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#2E7D32" },
  nameText: { fontWeight: 500 },

  barRow: { display: "flex", alignItems: "center", gap: 6 },
  barTrack: { height: 4, width: 56, borderRadius: 2, background: "var(--color-background-secondary)", overflow: "hidden" },
  barFill: { height: "100%" },
  barPct: { fontWeight: 700, fontFamily: "var(--font-mono)" },
  upsellPct: { fontWeight: 600, fontFamily: "var(--font-mono)" },

  // Drill sections
  staffGroup: { marginBottom: 12 },
  staffHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, padding: "6px 8px", background: "var(--color-background-secondary)", borderRadius: 6 },
  staffHeaderLeft: { display: "flex", alignItems: "center", gap: 7 },
  staffName: { fontWeight: 600, color: "var(--color-text-primary)" },
  staffCount: { color: "var(--color-text-tertiary)" },
  staffLost: { fontWeight: 700, color: P.nv, fontFamily: "var(--font-mono)" },

  incident: { cursor: "pointer", borderRadius: 6, padding: "7px 10px", marginBottom: 4 },
  incidentTop: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  incidentLeft: { display: "flex", alignItems: "center", gap: 8 },
  incidentRight: { display: "flex", alignItems: "center", gap: 8 },
  plateTag: { fontFamily: "var(--font-mono)", fontWeight: 700, color: "#2E7D32", background: "#E6F4E8", padding: "2px 7px", borderRadius: 4, border: "0.5px solid #BFE3C6" },
  guestText: { color: "var(--color-text-primary)" },
  dateText: { color: "var(--color-text-tertiary)" },
  lostText: { fontWeight: 700, color: P.nv, fontFamily: "var(--font-mono)" },
  caret: { color: "var(--color-text-secondary)" },

  refText: { fontFamily: "var(--font-mono)", color: "#3127B8", fontWeight: 600 },
  plateChip: { fontFamily: "var(--font-mono)", background: "var(--color-background-secondary)", padding: "1px 5px", borderRadius: 3, color: "var(--color-text-secondary)" },
  buzzDate: { color: "var(--color-text-secondary)" },

  drill: { marginTop: 8, paddingTop: 8, borderTop: "0.5px solid #BFE3C6" },
  drillGrid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 6 },
  drillGrid4: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6, marginBottom: 6 },
  drillTile: { background: "var(--color-background-primary)", borderRadius: 4, padding: "5px 7px", border: "0.5px solid #BFE3C6" },
  drillTileLabel: { color: "var(--color-text-tertiary)" },
  drillTileValue: { fontWeight: 600, color: P.nv },
  drillTileValueBuzz: { fontWeight: 600, color: "#3127B8" },
  drillReason: { color: P.nv, background: "var(--color-background-primary)", borderRadius: 4, padding: "6px 8px", border: "0.5px solid #BFE3C6" },
  reasonLabel: { fontWeight: 600 },
});

export default function StaffTab({ period }) {
  const cls = useStyles();
  const [focDrill, setFocDrill] = useState(null);
  const [buzzDrill, setBuzzDrill] = useState(null);

  const STAFF_DATA = [
    {n:"Priya K.",    dc:96, bk:142, upsells:38, upsellRev:1330, bo:2,  foc:1,  focLost:8,   buzzLost:12,  rv:4970,  sc:96},
    {n:"James T.",    dc:92, bk:118, upsells:22, upsellRev:770,  bo:4,  foc:4,  focLost:30,  buzzLost:24,  rv:4130,  sc:89},
    {n:"Elena R.",    dc:89, bk:131, upsells:19, upsellRev:665,  bo:6,  foc:6,  focLost:48,  buzzLost:34,  rv:4585,  sc:84},
    {n:"Marcus L.",   dc:82, bk:96,  upsells:11, upsellRev:385,  bo:11, foc:11, focLost:92,  buzzLost:68,  rv:3360,  sc:71},
    {n:"David W.",    dc:78, bk:88,  upsells:6,  upsellRev:210,  bo:14, foc:14, focLost:134, buzzLost:82,  rv:3080,  sc:64},
    {n:"Zoe M.",      dc:91, bk:109, upsells:29, upsellRev:1015, bo:3,  foc:2,  focLost:15,  buzzLost:18,  rv:3815,  sc:91},
  ];

  const byDrivo   = [...STAFF_DATA].sort((a,b)=>b.dc-a.dc);
  const byUpsells = [...STAFF_DATA].sort((a,b)=>b.upsells-a.upsells);
  const byScore   = [...STAFF_DATA].sort((a,b)=>b.sc-a.sc);
  const empOfMonth = byScore[0];

  const FOC_BY_STAFF = {
    "David W.": [
      {ref:"F-001",guest:"Room 302",plate:"UNK 445",bay:"A-03",time:"09:14",date:"10 Apr",lost:12,reason:"Guest complained about rate -- override issued"},
      {ref:"F-002",guest:"Room 518",plate:"TOY 221",bay:"B-07",time:"14:30",date:"9 Apr", lost:9, reason:"Conference attendee -- waived without authorisation"},
      {ref:"F-003",guest:"Room 110",plate:"HON 334",bay:"A-11",time:"11:05",date:"8 Apr", lost:11,reason:"Loyalty guest -- manager not consulted"},
    ],
    "Marcus L.": [
      {ref:"F-004",guest:"Room 401",plate:"BMW 901",bay:"C-02",time:"10:22",date:"11 Apr",lost:14,reason:"VIP -- override without documentation"},
      {ref:"F-005",guest:"Conf B",  plate:"BRT 555",bay:"Level A",time:"14:05",date:"11 Apr",lost:16,reason:"Event ran over -- no extension booked"},
    ],
    "Elena R.": [
      {ref:"F-006",guest:"Room 214",plate:"SUB 667",bay:"A-18",time:"16:40",date:"10 Apr",lost:8,reason:"Guest had DRIVO booking -- staff did not scan"},
    ],
  };

  const BUZZ_BY_STAFF = {
    "David W.": [
      {ref:"B-002",plate:"UNK 881",vehicle:"Unknown sedan",bay:"B-11",time:"09:22",date:"12 Apr",overstay:"4h 10m",lost:34,reason:"No booking found -- vehicle let through"},
    ],
    "Marcus L.": [
      {ref:"B-001",plate:"FOR 234",vehicle:"Ford Ranger",  bay:"A-07",time:"11:48",date:"12 Apr",overstay:"2h 30m",lost:18,reason:"Guest dispute -- refused to pay"},
      {ref:"B-003",plate:"BRT 555",vehicle:"Range Rover",  bay:"Level A",time:"14:05",date:"11 Apr",overstay:"1h 55m",lost:16,reason:"Event ran over"},
      {ref:"B-006",plate:"WAL 991",vehicle:"Hyundai i30",  bay:"C-06",time:"11:20",date:"3 Apr", overstay:"3h 10m",lost:26,reason:"Repeat vehicle -- prior incident"},
    ],
    "Elena R.": [
      {ref:"B-005",plate:"WAL 991",vehicle:"Hyundai i30",  bay:"C-06",time:"16:40",date:"10 Apr",overstay:"5h 45m",lost:46,reason:"Paid cash for 2h -- overstayed"},
    ],
    "James T.": [
      {ref:"B-004",plate:"MAZ 901",vehicle:"Mazda CX-5",   bay:"A-22",time:"13:12",date:"11 Apr",overstay:"3h 00m",lost:24,reason:"Late checkout -- parking not extended"},
    ],
  };

  return (
    <Box className={cls.root}>

      {/* Employee of Month banner */}
      <Box className={cls.banner}>
        <div>
          <Typography variant="body2" component="div" className={cls.bannerEyebrow}>EMPLOYEE OF THE MONTH</Typography>
          <Typography variant="h6" component="div" className={cls.bannerName}>{empOfMonth.n}</Typography>
          <Typography variant="body2" component="div" className={cls.bannerSub}>{"DRIVO Score: "+empOfMonth.sc+" . "+empOfMonth.dc+"% charge rate . "+empOfMonth.upsells+" upsells"}</Typography>
        </div>
        <Box className={cls.bannerRight}>
          <Typography variant="body2" component="div" className={cls.bannerRewardLabel}>Reward</Typography>
          <Typography variant="h5" component="div" className={cls.bannerReward}>$200</Typography>
          <Typography variant="body2" component="div" className={cls.bannerRewardSub}>Gift card -- {period}</Typography>
        </Box>
      </Box>

      <Box className={cls.grid}>

        {/* DRIVO % Leaderboard */}
        <Card title="DRIVO Charge Rate Leaderboard" subtitle={`Ranked by DRIVO % -- ${period}`}>
          <Table className={cls.table}>
            <TableHead><TableRow>{["#","Name","DRIVO %","Bookings","Revenue","Score"].map(h=>(
              <TableCell key={h} className={cls.th}><Typography variant="caption" component="span">{h}</Typography></TableCell>
            ))}</TableRow></TableHead>
            <TableBody>{byDrivo.map((s,i)=>(
              <TableRow key={s.n} className={cls.row} style={{background:i===0?"#E6F4E808":"transparent"}}>
                <TableCell className={cls.cellRank} style={{color:i===0?P.nv:i<3?"#3127B8":"var(--color-text-tertiary)"}}><Typography variant="body2" component="span">{i===0?"*":i+1}</Typography></TableCell>
                <TableCell className={cls.cell}><Box className={cls.nameCell}><Box className={cls.avatar}><Typography variant="caption" component="span">{ini(s.n)}</Typography></Box><Typography variant="body2" component="span" className={cls.nameText}>{s.n}</Typography></Box></TableCell>
                <TableCell className={cls.cell}>
                  <Box className={cls.barRow}>
                    <Box className={cls.barTrack}>
                      <div className={cls.barFill} style={{width:s.dc+"%",background:s.dc>=90?"#241C8A":s.dc>=85?"#3127B8":P.nv}} />
                    </Box>
                    <Typography variant="body2" component="span" className={cls.barPct} style={{color:s.dc>=90?"#241C8A":P.nv}}>{s.dc}%</Typography>
                  </Box>
                </TableCell>
                <TableCell className={cls.cellMono}><Typography variant="body2" component="span">{s.bk}</Typography></TableCell>
                <TableCell className={cls.cellMonoBold}><Typography variant="body2" component="span">{"$"+s.rv.toLocaleString()}</Typography></TableCell>
                <TableCell className={cls.cellScore} style={{color:s.sc>=90?"#241C8A":s.sc>=80?"#3127B8":P.nv}}><Typography variant="body2" component="span">{s.sc}</Typography></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </Card>

        {/* Upsells Leaderboard */}
        <Card title="Upsells Leaderboard" subtitle={`DRIVO upsells added per staff member -- ${period}`}>
          <Table className={cls.table}>
            <TableHead><TableRow>{["#","Name","Upsells","Revenue","Avg/Upsell","DRIVO %"].map(h=>(
              <TableCell key={h} className={cls.th}><Typography variant="caption" component="span">{h}</Typography></TableCell>
            ))}</TableRow></TableHead>
            <TableBody>{byUpsells.map((s,i)=>(
              <TableRow key={s.n} className={cls.row}>
                <TableCell className={cls.cellRank} style={{color:i===0?P.nv:i<3?"#3127B8":"var(--color-text-tertiary)"}}><Typography variant="body2" component="span">{i===0?"*":i+1}</Typography></TableCell>
                <TableCell className={cls.cell}><Box className={cls.nameCell}><Box className={cls.avatar}><Typography variant="caption" component="span">{ini(s.n)}</Typography></Box><Typography variant="body2" component="span" className={cls.nameText}>{s.n}</Typography></Box></TableCell>
                <TableCell className={cls.cellUpsells} style={{color:i===0?P.nv:"var(--color-text-primary)"}}><Typography variant="subtitle1" component="span">{s.upsells}</Typography></TableCell>
                <TableCell className={cls.cellMonoBold}><Typography variant="body2" component="span">{"$"+s.upsellRev.toLocaleString()}</Typography></TableCell>
                <TableCell className={cls.cellMono}><Typography variant="body2" component="span">{"$"+Math.round(s.upsellRev/s.upsells)}</Typography></TableCell>
                <TableCell className={cls.cell}>
                  <Typography variant="body2" component="span" className={cls.upsellPct} style={{color:s.dc>=90?"#241C8A":P.nv}}>{s.dc}%</Typography>
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </Card>

      </Box>

      {/* FOC and Buzz-outs Investigation */}
      <Box className={cls.grid}>

        <Card title="Free of Charge -- By Staff" subtitle={`Click any incident to investigate -- ${period}`}>
          {Object.entries(FOC_BY_STAFF).map(([name,records])=>(
            <Box key={name} className={cls.staffGroup}>
              <Box className={cls.staffHeader}>
                <Box className={cls.staffHeaderLeft}>
                  <Box className={cls.avatarSm}><Typography variant="caption" component="span">{ini(name)}</Typography></Box>
                  <Typography variant="body2" component="span" className={cls.staffName}>{name}</Typography>
                  <Typography variant="caption" component="span" className={cls.staffCount}>{records.length+" incidents"}</Typography>
                </Box>
                <Typography variant="body2" component="span" className={cls.staffLost}>{"$"+records.reduce((a,r)=>a+r.lost,0)+" lost"}</Typography>
              </Box>
              {records.map((r)=>(
                <Paper key={r.ref} elevation={0} onClick={()=>setFocDrill(focDrill?.ref===r.ref?null:r)} className={cls.incident} style={{border:"0.5px solid "+(focDrill?.ref===r.ref?"#241C8A":"var(--color-border-tertiary)"),background:focDrill?.ref===r.ref?"#ECEBF8":"var(--color-background-primary)"}}>
                  <Box className={cls.incidentTop}>
                    <Box className={cls.incidentLeft}>
                      <Typography variant="body2" component="span" className={cls.plateTag}>{r.plate}</Typography>
                      <Typography variant="body2" component="span" className={cls.guestText}>{r.guest}</Typography>
                    </Box>
                    <Box className={cls.incidentRight}>
                      <Typography variant="caption" component="span" className={cls.dateText}>{r.date+" "+r.time}</Typography>
                      <Typography variant="body2" component="span" className={cls.lostText}>{"$"+r.lost}</Typography>
                      <Typography variant="body2" component="span" className={cls.caret}>{focDrill?.ref===r.ref?"^":"v"}</Typography>
                    </Box>
                  </Box>
                  {focDrill?.ref===r.ref&&(
                    <Box className={cls.drill}>
                      <Box className={cls.drillGrid3}>
                        {[["Bay",r.bay],["Time",r.time],["Revenue Lost","$"+r.lost]].map(([l,v])=>(
                          <Box key={l} className={cls.drillTile}>
                            <Typography variant="caption" component="div" className={cls.drillTileLabel}>{l}</Typography>
                            <Typography variant="body2" component="div" className={cls.drillTileValue}>{v}</Typography>
                          </Box>
                        ))}
                      </Box>
                      <Typography variant="body2" component="div" className={cls.drillReason}>
                        <span className={cls.reasonLabel}>Reason: </span>{r.reason}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          ))}
        </Card>

        <Card title="Buzz-outs -- By Staff" subtitle={`Click any incident to investigate -- ${period}`}>
          {Object.entries(BUZZ_BY_STAFF).map(([name,records])=>(
            <Box key={name} className={cls.staffGroup}>
              <Box className={cls.staffHeader}>
                <Box className={cls.staffHeaderLeft}>
                  <Box className={cls.avatarSm}><Typography variant="caption" component="span">{ini(name)}</Typography></Box>
                  <Typography variant="body2" component="span" className={cls.staffName}>{name}</Typography>
                  <Typography variant="caption" component="span" className={cls.staffCount}>{records.length+" buzz-out"+(records.length>1?"s":"")}</Typography>
                </Box>
                <Typography variant="body2" component="span" className={cls.staffLost}>{"$"+records.reduce((a,r)=>a+r.lost,0)+" lost"}</Typography>
              </Box>
              {records.map((r)=>(
                <Paper key={r.ref} elevation={0} onClick={()=>setBuzzDrill(buzzDrill?.ref===r.ref?null:r)} className={cls.incident} style={{border:"0.5px solid "+(buzzDrill?.ref===r.ref?"#3127B8":"var(--color-border-tertiary)"),background:buzzDrill?.ref===r.ref?"#ECEBF8":"var(--color-background-primary)"}}>
                  <Box className={cls.incidentTop}>
                    <Box className={cls.incidentLeft}>
                      <Typography variant="caption" component="span" className={cls.refText}>{r.ref}</Typography>
                      <Typography variant="caption" component="span" className={cls.plateChip}>{r.plate}</Typography>
                      <Typography variant="caption" component="span" className={cls.buzzDate}>{r.date}</Typography>
                      <Typography variant="body2" component="span" className={cls.lostText}>{"$"+r.lost}</Typography>
                      <Typography variant="body2" component="span" className={cls.caret}>{buzzDrill?.ref===r.ref?"^":"v"}</Typography>
                    </Box>
                  </Box>
                  {buzzDrill?.ref===r.ref&&(
                    <Box className={cls.drill}>
                      <Box className={cls.drillGrid4}>
                        {[["Bay",r.bay],["Time",r.time],["Overstay",r.overstay],["Revenue Lost","$"+r.lost]].map(([l,v])=>(
                          <Box key={l} className={cls.drillTile}>
                            <Typography variant="caption" component="div" className={cls.drillTileLabel}>{l}</Typography>
                            <Typography variant="body2" component="div" className={cls.drillTileValueBuzz}>{v}</Typography>
                          </Box>
                        ))}
                      </Box>
                      <Typography variant="body2" component="div" className={cls.drillReason}>
                        <span className={cls.reasonLabel}>Reason: </span>{r.reason}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          ))}
        </Card>

      </Box>
    </Box>
  );
}
